import classNames from 'classnames';
import React, { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { BlockedContent } from '../../../../graphdebate/src/components/Blocking/BlockedContent';
import { ReportedContentBanner } from '../../../../graphdebate/src/components/Report/ReportedContentBanner';
import { loadLinkToolCss } from '../../editor/editorjs/load-link-tool-css';
import { useConnectedRouter } from '../../hooks/useConnectedRouter';
// import { useConnectedRouter } from '../../hooks/useConnectedRouter';
import { useWebsite } from '../../hooks/useWebsite';
import type { Author, UserReport, Vote } from '../../types/api';
import { NoSSR } from '../../utils/ssr';
import { isRemovedContent } from '../../utils/status';
import { isMobile } from '../../utils/web';
import { MessageContent } from '../Content/MessageContent/MessageContent';
import { MessageLimiter } from '../Content/MessageLimiter';
import { MessageLimiterDomParser } from '../Content/MessageLimiterDomParser';
import { ThreadOptionMenu } from '../HeaderActions/ThreadOptionMenu';
import { ThreadAuthoringDetails } from '../ThreadAuthoringDetails';
import { useThreadIsRoot } from '../ThreadContexts/ThreadIsRootContext';
import { getVoteIcon } from '../Vote/icons';

loadLinkToolCss();

export type ThreadInteractiveContentProps = {
  messageId?: string | undefined;
  mode?: 'nested';
  author: Author;
  answeredAuthor?: Author;
  date: Date;
  content: string;
  embeds: string | undefined;
  status: string;
  votes: Vote[];
  options: Record<string, React.ReactNode>;
  actions?: React.ReactNode;
  topIcon?: React.ReactNode;
  unlimited?: boolean;
  heightLimit?: number;
  topicSource?: React.ReactNode;
  isTopicMsg?: boolean;
  isNestedAnswer?: boolean;
  shouldHandleDsaDisplay?: boolean;
  isAuthorBlocked?: boolean;
  userReport?: UserReport;
};

export const ThreadInteractiveContent: React.FC<ThreadInteractiveContentProps> = ({
  messageId,
  mode,
  author,
  answeredAuthor,
  date,
  content,
  embeds,
  status,
  isTopicMsg,
  votes: rawVotes = [],
  options,
  actions,
  topIcon,
  unlimited,
  heightLimit = isMobile() ? 320 : 288,
  topicSource,
  isNestedAnswer,
  shouldHandleDsaDisplay,
  isAuthorBlocked = false,
  userReport,
}) => {
  const { graphdebate = {} } = useWebsite();
  const { query } = useConnectedRouter();
  const isRelevanceSort = query?.sort === 'relevance';
  const isContentReported = !!userReport?.createdAt;

  const { votesDiscreet: isVoteDiscreet } = graphdebate ?? {};
  const votes = (rawVotes || []).filter(vote =>
    isVoteDiscreet ? (vote.score as number) > 0 : vote.score !== 0,
  );

  const shouldAvoidLimiter = useMemo(
    () => unlimited || query?.cardRedirect,
    [query.cardRedirect, unlimited],
  );

  const mainScores = Object.entries(
    votes.reduce(
      (prev, vote) => ({
        ...prev,
        [`${vote.score}`]: (prev[`${vote.score}`] || 0) + 1,
      }),
      {} as Record<string, number>,
    ),
  )
    .map(([score]) => parseInt(score, 10))
    .slice(0, 3);

  const contentElt = useMemo(
    () => (
      <MessageContent
        content={content}
        embeds={embeds}
        status={status}
        shouldHandleDsaDisplay={shouldHandleDsaDisplay}
        withMedia
      />
    ),
    [content, embeds, status, shouldHandleDsaDisplay],
  );

  const hasBeenRemoved = isRemovedContent(status);
  const isRoot = useThreadIsRoot();

  const contentToDisplay = useMemo(
    () =>
      shouldAvoidLimiter ? (
        contentElt
      ) : (
        <>
          {isTopicMsg ? (
            <MessageLimiter limit={heightLimit}>{contentElt}</MessageLimiter>
          ) : (
            <MessageLimiterDomParser contentString={content} embeds={embeds}>
              {contentElt}
            </MessageLimiterDomParser>
          )}
        </>
      ),
    [shouldAvoidLimiter, contentElt, isTopicMsg, heightLimit, content, embeds],
  );

  const contentFooter = useMemo(
    () => (
      <div
        className={classNames(
          'content-footer flex items-center justify-between gap-2 mt-1',
          hasBeenRemoved && 'pointer-events-none opacity-0',
        )}
      >
        {votes.length !== 0 && (
          <div className="flex items-center gap-2">
            <div className="flex [&>*+*]:ml-[-5px]">
              {mainScores.map((score, index) => (
                <div
                  // eslint-disable-next-line
          key={index}
                  className="flex justify-center items-center bg-white shadow p-[3px] rounded-full"
                >
                  {getVoteIcon(score)}
                </div>
              ))}
            </div>
            <div className="text-gray-medium">{votes.length}</div>
          </div>
        )}
        {topicSource}
        {status === 'approved' && !isAuthorBlocked && (
          <div className="actionsContainer flex justify-end flex-1 text-base flex-wrap text-gray-silver">
            {actions}
          </div>
        )}
      </div>
    ),
    [hasBeenRemoved, votes, mainScores, topicSource, status, isAuthorBlocked, actions],
  );

  const actualContent = useMemo(
    () => (
      <>
        {contentToDisplay}
        {contentFooter}
      </>
    ),
    [contentToDisplay, contentFooter],
  );

  const fullContent = useMemo(() => {
    switch (true) {
      case isAuthorBlocked:
        return <BlockedContent />;
      case isContentReported:
        return (
          <ReportedContentBanner createdAt={userReport?.createdAt}>
            {actualContent}
          </ReportedContentBanner>
        );
      default:
        return actualContent;
    }
  }, [isAuthorBlocked, isContentReported, userReport?.createdAt, actualContent]);

  return (
    <div
      className={twMerge(
        'ThreadInteractiveContent editor space-y-2.5',
        hasBeenRemoved && 'pointer-events-none',
      )}
    >
      {/* items-center relative -mr-1 */}
      <div className="ThreadInteractiveContentHeader flex justify-between gap-x-2">
        <div className={'threadMsgChildAuthor flex flex-1 min-w-0'}>
          <div className="flex space-x-2 min-w-0">
            <div
              className={twMerge(
                'AuthoringDetails flex items-center gap-2 min-w-0',
                !!answeredAuthor && 'flex-col items-start',
                isNestedAnswer && !isRoot && 'flex-col items-start gap-0',
                !isRelevanceSort && 'items-start',
              )}
            >
              <ThreadAuthoringDetails
                date={date}
                author={author}
                answeredAuthor={answeredAuthor}
                isNestedAnswer={isNestedAnswer}
                noCenter
                isRelevanceSort={isRelevanceSort}
              />
            </div>
          </div>
        </div>

        <div
          className={twMerge(
            'threadMessageChildActions flex items-center -mt-3 -mr-3 md:-mt-1 md:-mr-1',
            answeredAuthor && 'items-start',
            mode,
            isNestedAnswer ? 'isNestedAnswer' : 'isNotNestedAnswer',
          )}
        >
          <div className="ThreadOptionMenu flex flex-col-reverse md:flex-row items-center justify-center relative">
            {topIcon}
            <NoSSR>
              {status === 'approved' && (
                <ThreadOptionMenu
                  messageId={messageId}
                  isYakliDisplay={isNestedAnswer}
                  {...options}
                />
              )}
            </NoSSR>
          </div>
        </div>
      </div>

      <div className="ThreadMessageContent relative flex-1">{fullContent}</div>
    </div>
  );
};
